import site from '../../site';
import getCurrentLocale from '../../site-locales/selectors/current-locale';
import view from '../../view';
import hdrm from '../../hdrm';
import handlers from '../handlers';
import blocks from '../../blocks';
import dialogs from '../../dialogs';
import findFirstFor from '../../constraints/filters/find-first-for';

const after = (store) => (next) => (action) => {
  const { type, payload } = action;
  const state = store.getState();

  switch (type) {
    case site.actionTypes.CREATE_PAGE:
    case site.actionTypes.COPY_PAGE: {
      next(action);
      handlers.onAfterAddNewPage(payload.pageId);
      break;
    }

    case site.actionTypes.DELETE_PAGE: {
      next(action);
      handlers.onAfterDeletePage(payload.pageId);
      break;
    }

    case site.actionTypes.SET_HOME_PAGE: {
      const currentLocale = getCurrentLocale(state);
      const oldHomePage = site.selectors.getHomePage(state, currentLocale);
      const oldHomePageId = oldHomePage ? oldHomePage.id : null;

      next(action);
      handlers.onAfterChangeHomePage(oldHomePageId, payload.pageId);
      break;
    }

    case site.actionTypes.UPDATE_PAGE_SETTINGS: {
      next(action);
      handlers.onAfterUpdatePageSettings(payload.pageId);
      break;
    }

    case site.actionTypes.CONNECT_SITE: {
      next(action);
      handlers.onAfterSiteConnect();
      break;
    }

    case view.actionTypes.INSERT_BLOCK: {
      const { blockId, variationId, refElementId } = payload;

      const targetElement = view.accessors.getParentLiveElement(refElementId);
      const targetBlock = targetElement.closest(`[id^='${blocks.constants.BLOCK_ID_PREFIX}']`);
      const targetBlockId = blocks.accessors.getBlockIdByElement(targetBlock);
      const targetBlockVariationId = blocks.accessors.getVariationIdByElement(targetBlock);

      next(action);
      handlers.onAfterBlockAdded({
        blockId,
        variationId,
        targetBlockId,
        targetBlockVariationId,
      });
      break;
    }

    case view.actionTypes.DUPLICATE_ELEMENT: {
      const { elementId } = payload;
      const element = view.accessors.getLiveElement(elementId);
      const blockId = blocks.accessors.getBlockIdByElement(element);
      const variationId = blocks.accessors.getVariationIdByElement(element);
      const constraint = findFirstFor(element);
      const constraintId = constraint && constraint.id;

      const parentElement = view.accessors.getParentLiveElement(elementId);
      const parentElementId = hdrm.accessors.getClosestReferenceId(parentElement);
      const closestBlock = blocks.accessors.getClosestBlockByElementId(parentElementId);
      const targetBlockId = blocks.accessors.getBlockIdByElement(closestBlock);
      const targetBlockVariationId = blocks.accessors.getVariationIdByElement(closestBlock);

      next(action);
      handlers.onAfterBlockDuplicated({
        blockId,
        variationId,
        elementId,
        constraintId,
        targetBlockId,
        targetBlockVariationId,
      });
      break;
    }

    case view.actionTypes.DELETE_ELEMENT: {
      const { blockId, variationId } = payload;

      next(action);
      handlers.onAfterBlockDeleted({ blockId, variationId });
      break;
    }

    case view.actionTypes.SHIFT_ELEMENT: {
      const { elementId, refElementId, position, previousPositionData } = action.payload;
      const element = view.accessors.getLiveElement(elementId);
      const blockId = blocks.accessors.getBlockIdByElement(element);
      const variationId = blocks.accessors.getVariationIdByElement(element);
      const constraint = findFirstFor(element);
      const constraintId = constraint && constraint.id;

      const currentPositionData = {
        refElementId,
        position,
      };

      next(action);
      handlers.onAfterBlockShifted({
        blockId,
        variationId,
        previousPosition: previousPositionData,
        currentPosition: currentPositionData,
        elementId,
        constraintId,
      });
      break;
    }

    case view.actionTypes.BLUR_FOCUSED_ELEMENT: {
      const focusedElementId = view.selectors.getFocusedElement(state);
      const liveElement = view.accessors.getLiveElement(focusedElementId);

      next(action);
      handlers.onAfterBlurFocusedElement(liveElement);
      break;
    }

    case view.actionTypes.COPY_ELEMENT: {
      const { elementId } = payload;
      const element = view.accessors.getLiveElement(elementId);
      const blockId = blocks.accessors.getBlockIdByElement(element);
      const variationId = blocks.accessors.getVariationIdByElement(element);

      next(action);
      handlers.onAfterBlockCopied({ blockId, variationId });
      break;
    }

    case view.actionTypes.CUT_ELEMENT: {
      const { elementId } = payload;
      const oldElement = view.accessors.getLiveElement(elementId);
      const blockId = blocks.accessors.getBlockIdByElement(oldElement);
      const variationId = blocks.accessors.getVariationIdByElement(oldElement);

      next(action);
      handlers.onAfterBlockCutout({ blockId, variationId });
      break;
    }

    case view.actionTypes.PASTE_ELEMENT: {
      const { refElementId, position } = payload;

      next(action);
      handlers.onAfterBlockPasted({ refElementId, position });
      break;
    }

    case view.actionTypes.SET_FOCUSED_ELEMENT: {
      const { elementId } = payload;

      next(action);
      handlers.onAfterElementFocused({ elementId });
      break;
    }

    case view.actionTypes.SET_SELECTED_ELEMENT: {
      const { elementId, options } = payload;

      next(action);
      handlers.onAfterElementSelected({ elementId, options });
      break;
    }

    case dialogs.actionTypes.HIDE_DIALOG: {
      next(action);
      handlers.onAfterDialogHide();
      break;
    }

    default:
      next(action);
      break;
  }
};

export default after;
